import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Droplet, ArrowLeft, Mail, AlertTriangle } from "lucide-react";
import { POLICIES, POLICY_LINKS } from "@/data/policies";
import { useBusiness } from "@/lib/useBusiness";
import Seo from "@/components/Seo";

export default function PolicyPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const policy = POLICIES[slug];
  const biz = useBusiness();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!policy) {
    return (
      <div className="min-h-screen flex items-center justify-center px-5" data-testid="policy-not-found">
        <div className="text-center">
          <h1 className="font-chivo font-extrabold tracking-tight text-3xl text-slate-950">Page not found</h1>
          <button
            onClick={() => navigate("/")}
            className="mt-6 bg-slate-950 text-white px-6 py-3 font-plexmono text-xs tracking-[0.2em] uppercase hover:bg-slate-800 transition-colors"
            data-testid="policy-not-found-home"
          >
            Back to Lab2o
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="policy-page">
      <Seo
        title={`${policy.title} | Lab2o`}
        path={`/policies/${slug}`}
        description={`${policy.title} for Lab2o research-grade bacteriostatic water. For research purposes only.`}
      />
      <header className="border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="policy-logo">
            <span className="w-7 h-7 bg-slate-950 text-white flex items-center justify-center rounded-sm">
              <Droplet className="w-4 h-4" strokeWidth={2.2} />
            </span>
            <span className="font-chivo font-extrabold tracking-tight text-lg">
              LAB<span className="text-slate-400">2O</span>
            </span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 font-plexmono text-[11px] tracking-[0.15em] uppercase text-slate-500 hover:text-slate-950 transition-colors"
            data-testid="policy-back-link"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to site
          </Link>
        </div>
      </header>

      <main className="flex-1 max-w-4xl mx-auto px-5 sm:px-8 py-14 sm:py-20 w-full">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="font-plexmono text-[11px] tracking-[0.25em] uppercase text-slate-400 mb-4" data-testid="policy-overline">
            Lab2o — Legal · Last updated {policy.updated}
          </p>
          <h1 className="font-chivo font-black tracking-tighter text-4xl sm:text-5xl text-slate-950 mb-12" data-testid="policy-title">
            {policy.title}
          </h1>

          <div className="flex flex-col">
            {policy.sections.map((s, i) => (
              <motion.section
                key={s.heading}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 + i * 0.05 }}
                className="py-7 border-t border-slate-200 grid sm:grid-cols-12 gap-3 sm:gap-8"
                data-testid={`policy-section-${i + 1}`}
              >
                <h2 className="sm:col-span-4 font-chivo font-bold text-lg text-slate-950">
                  <span className="font-plexmono text-[10px] tracking-[0.2em] text-slate-400 block mb-1.5">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  {s.heading}
                </h2>
                <p className="sm:col-span-8 text-sm sm:text-base text-slate-600 leading-relaxed">{s.body}</p>
              </motion.section>
            ))}
          </div>

          <div className="mt-12 border-l-2 border-red-600 bg-red-50/60 px-5 py-4 flex items-start gap-3" data-testid="policy-disclaimer">
            <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
            <p className="text-red-700 text-sm font-medium leading-snug">
              For research purposes only. Not for human or veterinary use or consumption.
            </p>
          </div>
        </motion.div>
      </main>

      <footer className="border-t border-slate-200 bg-slate-50">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-10">
          <div className="grid sm:grid-cols-2 gap-8">
            <div className="text-sm text-slate-600 flex flex-col gap-2" data-testid="policy-footer-contact">
              <span className="font-medium text-slate-700" data-testid="policy-footer-business-name">{biz.legalName}</span>
              <span className="flex items-center gap-2.5" data-testid="policy-footer-email">
                <Mail className="w-4 h-4 text-slate-400 shrink-0" />
                {biz.email}
              </span>
            </div>
            <nav className="flex flex-col gap-2 sm:items-end" data-testid="policy-footer-links">
              {POLICY_LINKS.filter((l) => l.slug !== slug).map((l) => (
                <Link
                  key={l.slug}
                  to={`/policies/${l.slug}`}
                  className="text-sm text-slate-500 hover:text-slate-950 transition-colors"
                  data-testid={`policy-nav-${l.slug}`}
                >
                  {l.label}
                </Link>
              ))}
            </nav>
          </div>
          <p className="mt-8 pt-6 border-t border-slate-200 text-xs text-slate-400">
            © 2026 {biz.legalName}. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
